import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { Badge } from '../../components/Badge';
import { HelpTip } from '../../components/HelpTip';
import type { Detalle, Flujo, Semana } from './types';
import { diaMes, etiquetaTipo, fechaCorta, pesos, pesosCorto } from './formato';

const HORIZONTES = [8, 13, 26];

function LinkReferencia({ r }: { r: Detalle['referencia'] }) {
  if (!r) return <span className="text-slate-400">—</span>;
  if (r.tipo === 'factura') return <Link to={`/facturas?factura=${r.id}`} className="text-brand-600 hover:underline">Factura #{r.id}</Link>;
  if (r.tipo === 'orden_compra') return <Link to="/compras" className="text-brand-600 hover:underline" title="Ver en Órdenes de Compra">OC #{r.id}</Link>;
  if (r.tipo === 'orden_venta') return <Link to="/ventas" className="text-brand-600 hover:underline" title="Ver en Ventas">OV #{r.id}</Link>;
  const etiquetas: Record<string, string> = { vencimiento: 'Vencimiento', gasto_recurrente: 'Gasto', factura_venta: 'Factura de venta', caja_ingreso: 'Ingreso de caja' };
  return <span className="text-slate-600">{etiquetas[r.tipo] ?? r.tipo} #{r.id}</span>;
}

// ============================================================
// Proyección
// ============================================================

interface Seleccion { titulo: string; items: Detalle[]; }

export function Proyeccion({ data, loading, error, semanas, setSemanas, incluirComprometido, setIncluirComprometido }: {
  data: Flujo | null; loading: boolean; error: string | null;
  semanas: number; setSemanas: (n: number) => void; incluirComprometido: boolean; setIncluirComprometido: (b: boolean) => void;
}) {
  const [sel, setSel] = useState<Seleccion | null>(null);

  const abrir = (s: Semana, titulo: string, filtro: (d: Detalle) => boolean) => {
    const items = s.detalle.filter(filtro);
    if (items.length > 0) setSel({ titulo: `${titulo} · semana del ${fechaCorta(s.fechaDesde)}`, items });
  };

  const celda = (key: number, valor: number, onClick: () => void, cls = '') => (
    <td key={key} className={`px-3 py-1.5 text-right whitespace-nowrap ${cls}`}>
      {Math.abs(valor) < 0.005 ? <span className="text-slate-300">—</span> : (
        <button type="button" onClick={onClick} className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">{pesosCorto(valor)}</button>
      )}
    </td>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          Horizonte
          <select value={semanas} onChange={e => setSemanas(Number(e.target.value))} className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm">
            {HORIZONTES.map(h => <option key={h} value={h}>{h} semanas</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={incluirComprometido} onChange={e => setIncluirComprometido(e.target.checked)} className="w-4 h-4" />
          Incluir comprometido (OC)
          <HelpTip tips={[
            'Lo que falta facturar de las OCs abiertas (no canceladas ni cerradas): (pedido − facturado) × precio con descuentos, con los impuestos de la OC (o el IVA de cada línea si la OC no tiene impuestos cargados), menos el anticipo ya facturado',
            'OCs con facturas viejas sin items: se toma lo menor entre ese cálculo y el total de la OC menos lo ya facturado',
            'Fecha estimada: según la condición de pago de la OC, desde la fecha de entrega (o de la OC si la condición se cuenta desde la OC); si la entrega ya pasó, desde hoy',
          ]} />
        </label>
      </div>

      {error ? <p className="text-sm text-red-600">No se pudo cargar el flujo de caja: {error}</p>
      : loading || !data ? <div className="text-center py-12 text-slate-400" role="status">Cargando...</div> : (<>
        {data.avisos.length > 0 && (
          <ul className="space-y-1.5">
            {data.avisos.map((a, i) => (
              <li key={i} className={`text-sm px-3 py-2 rounded-lg border ${a.tipo === 'saldo_negativo' || a.tipo === 'fuente_error' ? 'bg-red-50 border-red-200 text-red-800' : a.tipo === 'fuente_pendiente' ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                {a.mensaje}
              </li>
            ))}
          </ul>
        )}

        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Saldo inicial por cuenta · {pesos(data.saldoInicial)}</h2>
          {data.cuentas.length === 0 ? <p className="text-sm text-slate-500">No hay cuentas activas. Creá una en "Cuentas y saldos" y cargale el saldo.</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {data.cuentas.map(c => (
                <div key={c.id} className={`bg-white rounded-xl border p-3 shadow-sm ${c.viejo || c.sinSaldo ? 'border-amber-300' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-slate-700 truncate">{c.nombre}</p>
                    <Badge tone={c.tipo === 'mercadopago' ? 'blue' : 'slate'}>{etiquetaTipo(c.tipo)}</Badge>
                  </div>
                  <p className={`text-xl font-bold ${c.saldo < 0 ? 'text-red-600' : 'text-slate-800'}`}>{pesos(c.saldo)}</p>
                  <p className="text-xs text-slate-500">
                    {c.sinSaldo ? <span className="text-amber-700">Sin saldo cargado</span>
                      : <>al {fechaCorta(c.fecha as string)}{c.origen === 'automatico' ? ' · automático' : ''}{c.viejo && <span className="text-amber-700"> · hace {c.diasAntiguedad} días</span>}</>}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="table-wrap overflow-x-auto bg-white rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[14rem]">Concepto</th>
                {data.semanas.map(s => (
                  <th key={s.indice} className="text-right px-3 py-2 whitespace-nowrap" title={`${fechaCorta(s.fechaDesde)} al ${fechaCorta(s.fechaHasta)}`}>
                    {s.indice === 0 ? 'Actual' : diaMes(s.fechaDesde)}
                    <span className="block text-[10px] font-normal text-slate-400">{diaMes(s.fechaDesde)}–{diaMes(s.fechaHasta)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t bg-slate-50/60 font-medium">
                <td className="px-3 py-1.5 sticky left-0 bg-slate-50 z-10">Saldo inicial</td>
                {data.semanas.map(s => <td key={s.indice} className={`px-3 py-1.5 text-right whitespace-nowrap ${s.saldoInicial < 0 ? 'text-red-600' : ''}`}>{pesosCorto(s.saldoInicial)}</td>)}
              </tr>

              {(['ingreso', 'egreso'] as const).map(tipo => {
                const filas = tipo === 'ingreso' ? data.filas.ingresos : data.filas.egresos;
                const atrasadoTotal = tipo === 'ingreso' ? data.atrasado.totalIngresos : data.atrasado.totalEgresos;
                const color = tipo === 'ingreso' ? 'text-green-700' : 'text-red-700';
                return [
                  <tr key={`${tipo}-total`} className="border-t font-semibold">
                    <td className={`px-3 py-1.5 sticky left-0 bg-white z-10 ${color}`}>{tipo === 'ingreso' ? 'Ingresos' : 'Egresos'}</td>
                    {data.semanas.map(s => celda(s.indice, tipo === 'ingreso' ? s.totalIngresos : s.totalEgresos,
                      () => abrir(s, tipo === 'ingreso' ? 'Ingresos' : 'Egresos', d => d.tipo === tipo), color))}
                  </tr>,
                  ...filas.map(f => (
                    <tr key={`${tipo}-${f.clave}`} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 pl-6 sticky left-0 bg-white z-10 text-slate-600">
                        {f.etiqueta}
                        {f.clave === 'cobros_mercadolibre' && <HelpTip tips={[
                          'Pagos de ventas de MercadoLibre aprobados y todavía no liberados, por su monto neto, el día en que MercadoPago libera el dinero (fecha real, no estimada)',
                          'Lo ya liberado está en el saldo de la cuenta MercadoPago. Los pagos sin fecha de liberación se informan aparte en los avisos',
                          'Se sincroniza todos los días a la madrugada o con "Actualizar cobros de MercadoPago" en Cuentas y saldos',
                        ]} />}
                      </td>
                      {data.semanas.map(s => celda(s.indice, (tipo === 'ingreso' ? s.ingresos : s.egresos)[f.clave] ?? 0,
                        () => abrir(s, f.etiqueta, d => d.tipo === tipo && d.clave === f.clave)))}
                    </tr>
                  )),
                  atrasadoTotal > 0 && (
                    <tr key={`${tipo}-atrasado`} className="border-t border-slate-100 italic">
                      <td className="px-3 py-1.5 pl-6 sticky left-0 bg-white z-10 text-amber-700" title="Impago con fecha pasada: ya está sumado en la semana actual">Atrasado (incluido en la semana actual)</td>
                      {data.semanas.map(s => s.indice === 0
                        ? celda(s.indice, atrasadoTotal, () => abrir(s, 'Atrasado', d => d.tipo === tipo && d.atrasado), 'text-amber-700')
                        : <td key={s.indice} className="px-3 py-1.5" />)}
                    </tr>
                  ),
                ];
              })}

              <tr className="border-t font-semibold">
                <td className="px-3 py-1.5 sticky left-0 bg-white z-10">Neto</td>
                {data.semanas.map(s => <td key={s.indice} className={`px-3 py-1.5 text-right whitespace-nowrap ${s.neto < 0 ? 'text-red-600' : 'text-green-700'}`}>{pesosCorto(s.neto)}</td>)}
              </tr>
              <tr className="border-t-2 border-slate-300 font-bold">
                <td className="px-3 py-2 sticky left-0 bg-white z-10">Saldo final</td>
                {data.semanas.map(s => (
                  <td key={s.indice} className={`px-3 py-2 text-right whitespace-nowrap ${s.saldoFinal < 0 ? 'text-red-600 bg-red-50' : 'text-slate-800'}`}>{pesosCorto(s.saldoFinal)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {(data.fueraDeHorizonte.ingresos > 0 || data.fueraDeHorizonte.egresos > 0) && (
          <p className="text-xs text-slate-500">
            Después del horizonte: ingresos {pesos(data.fueraDeHorizonte.ingresos)} · egresos {pesos(data.fueraDeHorizonte.egresos)} (no incluidos en la tabla).
          </p>
        )}
      </>)}

      <Modal open={!!sel} onClose={() => setSel(null)} title={sel?.titulo} size="lg">
        {sel && (
          <div className="table-wrap overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr>
                <th className="text-left px-3 py-2">Fecha</th><th className="text-left px-3 py-2">Descripción</th>
                <th className="text-left px-3 py-2">Origen</th><th className="text-right px-3 py-2">Monto</th>
              </tr></thead>
              <tbody>
                {sel.items.map((d, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(d.fecha)} {d.atrasado && <Badge tone="amber">atrasado</Badge>}</td>
                    <td className="px-3 py-2">{d.descripcion}</td>
                    <td className="px-3 py-2 whitespace-nowrap"><LinkReferencia r={d.referencia} /></td>
                    <td className={`px-3 py-2 text-right whitespace-nowrap font-medium ${d.tipo === 'ingreso' ? 'text-green-700' : 'text-red-700'}`}>{pesos(d.monto)}</td>
                  </tr>
                ))}
                <tr className="border-t bg-slate-50 font-semibold">
                  <td className="px-3 py-2" colSpan={3}>Total ({sel.items.length})</td>
                  <td className="px-3 py-2 text-right">{pesos(sel.items.reduce((s, d) => s + (d.tipo === 'ingreso' ? d.monto : -d.monto), 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
